import { useSyncExternalStore } from "react";
import { doc, onSnapshot, type Firestore } from "firebase/firestore";
import { getFirebaseFirestore } from "./firebase";
import { subscribeAuth } from "./authClient";
import { getPremiumState, usePremium, type PremiumState } from "./premium";
import { BILLING_CONFIG } from "./billingConfig";
import { captureException } from "./telemetry";

/**
 * AI usage meter — client-side subscription to `users/{uid}/usage/{period}`
 * with a friendly hook surface. Powers the in-app meter widget and the
 * inline budget warnings on the AI sheets.
 *
 * The Firestore doc is written ONLY by Cloud Functions (`Admin SDK` bypass)
 * and the rule denies client writes. Reads are owner-only.
 *
 * Optimistic updates: every successful AI callable returns a `usage`
 * envelope on the response. Callers can pipe that through
 * {@link recordOptimisticUsage} so the meter updates instantly without
 * waiting for the Firestore snapshot to propagate (usually <1s, but worth
 * the polish on a sub-second AI call).
 */

const USAGE_COLLECTION = "usage";

/**
 * Compute the same period key the server uses for the current usage doc.
 * MUST stay byte-for-byte identical to `periodKey()` in
 * [functions/src/billing/usage.ts](../../functions/src/billing/usage.ts).
 *
 * Uses UTC, not local time, so two devices in different zones (e.g. a
 * laptop in NYC and a phone in Tokyo) end up reading the same doc.
 */
export function currentPeriodKey(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

/** Stored shape of the usage doc on Firestore. Mirror of `UsageDoc` in
 *  the server module. */
interface UsageDocData {
  periodKey?: unknown;
  plan?: unknown;
  count?: unknown;
  overageCount?: unknown;
  includedQuota?: unknown;
}

/** Envelope sent on every successful AI callable response. Server-side
 *  mirror in `functions/src/billing/usage.ts > UsageResponseEnvelope`. */
export interface UsageResponseEnvelope {
  countAfter: number;
  isOverage: boolean;
  includedQuota: number;
  plan: "free" | "premium";
}

/**
 * Public snapshot exposed by {@link useAiUsage}. Derived state — never
 * stored as-is. Computed every render from raw usage + premium state.
 */
export interface AiUsageSnapshot {
  /** Loading: still waiting for the first Firestore snapshot OR for the
   *  premium claim to resolve. Treat as "unknown" — don't render a usage
   *  bar yet. */
  status: "loading" | "ready" | "signed-out" | "unavailable";
  /** Tier driving the limit calculation, from the live premium claim. */
  plan: "free" | "premium";
  /** Total successful AI calls in the current UTC period. */
  used: number;
  /** Included quota for this tier this period. */
  included: number;
  /** `max(0, used - included)`. */
  overageCount: number;
  /** Projected overage cost in USD: `overageCount × overagePriceUsdPerCredit`.
   *  Always 0 for free users. */
  projectedOverageCostUsd: number;
  /** Period key (e.g. "2026-05"). For the "Resets monthly" hint. */
  periodKey: string;
  /** Convenience: `used >= included` for both tiers. Drives the
   *  "you're at your limit" (free) and "you're past included" (premium) UI. */
  atOrOverIncluded: boolean;
}

/**
 * Internal raw state — what the singleton tracks from Firestore.
 * Decoupled from the public snapshot because the snapshot also depends
 * on the live premium claim, which lives in a different store.
 */
interface RawUsage {
  /** Whether we've received at least one Firestore snapshot for the
   *  current uid (or definitively know there's no signed-in user). */
  loaded: boolean;
  /** Total count from Firestore, or 0 if the doc doesn't exist. */
  count: number;
  /** UID this raw state corresponds to (null when signed out). */
  uid: string | null;
}

const INITIAL_RAW: RawUsage = { loaded: false, count: 0, uid: null };
const SIGNED_OUT_RAW: RawUsage = { loaded: true, count: 0, uid: null };

// Singleton: one Firestore subscription shared across every consumer of
// `useAiUsage`. We do this so dropping the meter widget into the AI
// sheets and the Settings panel costs one Firestore listener, not three.
let rawUsage: RawUsage = INITIAL_RAW;
let optimisticCount: number | null = null;
let subscribed = false;
let unsubAuth: (() => void) | null = null;
let unsubDoc: (() => void) | null = null;
let listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Tear down the active per-uid Firestore subscription, if any. */
function unsubFromDoc(): void {
  if (unsubDoc) {
    unsubDoc();
    unsubDoc = null;
  }
}

function setRaw(next: RawUsage): void {
  // Identity-compare to skip pointless notifies.
  if (
    rawUsage.loaded === next.loaded &&
    rawUsage.count === next.count &&
    rawUsage.uid === next.uid
  ) {
    return;
  }
  rawUsage = next;
  notify();
}

/** Subscribe the singleton to a specific user's usage doc. Idempotent —
 *  if `uid` matches the current subscription, no-op. */
function subscribeToUid(db: Firestore, uid: string): void {
  if (rawUsage.uid === uid && unsubDoc !== null) return;
  unsubFromDoc();
  // Reset to "loading for this uid" — keep `loaded` false until the
  // first snapshot arrives.
  rawUsage = { loaded: false, count: 0, uid };
  optimisticCount = null;
  notify();

  const ref = doc(db, "users", uid, USAGE_COLLECTION, currentPeriodKey());
  unsubDoc = onSnapshot(
    ref,
    (snap) => {
      let count = 0;
      if (snap.exists()) {
        const data = snap.data() as UsageDocData;
        if (typeof data.count === "number" && Number.isFinite(data.count)) {
          count = Math.max(0, data.count);
        }
      }
      // The server snapshot supersedes any pending optimistic value
      // once it's caught up (or surpassed it — e.g. another tab also
      // burned a credit).
      if (optimisticCount !== null && count >= optimisticCount) {
        optimisticCount = null;
      }
      setRaw({ loaded: true, count, uid });
    },
    (err) => {
      // Race: user signed out mid-subscription. Fall back to a "loaded
      // empty" state — the auth listener will re-route to signed-out
      // on the next tick.
      console.warn("[usage] usage doc subscription error", err);
      captureException(err, { context: "usage.subscribeToUid", uid });
      setRaw({ loaded: true, count: 0, uid });
    }
  );
}

function ensureSubscribed(): void {
  if (subscribed) return;
  subscribed = true;

  const db = getFirebaseFirestore();
  if (!db) {
    // No Firebase configured. Mark loaded so the snapshot stops
    // saying "loading" — the AI callables are also unavailable in
    // this state, and the UsageMeter widget will degrade based on
    // the premium "unavailable" status.
    setRaw(SIGNED_OUT_RAW);
    return;
  }

  unsubAuth = subscribeAuth((user) => {
    // Anonymous sessions have a uid but no allowance. Subscribing would
    // show a free-account balance they cannot spend.
    const uid = meteredAccountUid(user);
    if (!uid) {
      unsubFromDoc();
      optimisticCount = null;
      setRaw(SIGNED_OUT_RAW);
      return;
    }
    subscribeToUid(db, uid);
  });
}

function subscribeRaw(listener: () => void): () => void {
  ensureSubscribed();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getRawSnapshot(): RawUsage {
  return rawUsage;
}

function getRawServerSnapshot(): RawUsage {
  return INITIAL_RAW;
}

/**
 * Uid whose usage doc the meter may read, or null when there is nothing
 * to meter. Anonymous sessions have a uid but no allowance — a cleared
 * browser mints a new one — so they stay on the signed-out snapshot and
 * never see a free-credit balance.
 */
export function meteredAccountUid(
  user: { uid: string; isAnonymous?: boolean } | null
): string | null {
  if (!user || user.isAnonymous) return null;
  return user.uid;
}

/**
 * Pure derivation: given a raw usage value, the optimistic overlay, and
 * the current premium state, produce the public snapshot. Exported for
 * unit tests; production callers should use the hook.
 */
export function deriveAiUsageSnapshot(
  raw: RawUsage,
  optimistic: number | null,
  premium: PremiumState
): AiUsageSnapshot {
  if (premium.status === "unavailable") {
    return {
      status: "unavailable",
      plan: "free",
      used: 0,
      included: BILLING_CONFIG.freeMonthlyCredits,
      overageCount: 0,
      projectedOverageCostUsd: 0,
      periodKey: currentPeriodKey(),
      atOrOverIncluded: false,
    };
  }
  if (premium.status === "signed-out" || raw.uid === null) {
    return {
      status: "signed-out",
      plan: "free",
      used: 0,
      included: BILLING_CONFIG.freeMonthlyCredits,
      overageCount: 0,
      projectedOverageCostUsd: 0,
      periodKey: currentPeriodKey(),
      atOrOverIncluded: false,
    };
  }

  const stillLoading = !raw.loaded || premium.status === "loading";
  // A UI-only override must not pretend the server will honor Premium
  // quota. When the published state is overridden, bill against the
  // real stripeRole claim. After the Auth emulator stamps the claim,
  // `claimIsPremium` is true and the meter matches the server.
  const isPremium = premium.overridden
    ? premium.claimIsPremium || premium.trial
    : premium.isPremium;
  const plan: "free" | "premium" = isPremium ? "premium" : "free";
  const included = isPremium
    ? BILLING_CONFIG.premiumIncludedCredits
    : BILLING_CONFIG.freeMonthlyCredits;
  const used = Math.max(
    0,
    optimistic !== null ? Math.max(optimistic, raw.count) : raw.count
  );
  const overageCount = Math.max(0, used - included);
  // A reverse-trial account is capped at the allowance (nothing to bill
  // overage against), so there is no projected cost to show.
  const projectedOverageCostUsd =
    isPremium && !premium.trial ? overageCount * BILLING_CONFIG.overagePriceUsdPerCredit : 0;

  return {
    status: stillLoading ? "loading" : "ready",
    plan,
    used,
    included,
    overageCount,
    projectedOverageCostUsd,
    periodKey: currentPeriodKey(),
    atOrOverIncluded: used >= included,
  };
}

/**
 * React hook — current AI usage snapshot for the signed-in user.
 *
 * Re-renders when the underlying Firestore doc changes, when the user
 * signs in/out, when the premium claim refreshes, or when
 * {@link recordOptimisticUsage} is called.
 *
 * Returns a "loading" snapshot during the first auth resolution and the
 * first Firestore read; consumers should branch on `status` rather than
 * reading raw counts in the loading state.
 */
export function useAiUsage(): AiUsageSnapshot {
  // Two stores composed: raw usage from this module's singleton, premium
  // claim from the auth-token-derived store. We deliberately call both
  // hooks (rather than collapsing them) so React's commit-phase
  // observer wires up both subscriptions.
  const raw = useSyncExternalStore(
    subscribeRaw,
    getRawSnapshot,
    getRawServerSnapshot
  );
  const premium = usePremium();
  return deriveAiUsageSnapshot(raw, optimisticCount, premium);
}

/** Sync read — same shape as the hook, for non-React callers (e.g. the
 *  click handler that decides whether to open the AI sheet or warn the
 *  user that they're about to enter overage). */
export function getAiUsageSync(): AiUsageSnapshot {
  ensureSubscribed();
  return deriveAiUsageSnapshot(rawUsage, optimisticCount, getPremiumState());
}

/**
 * Pure decision: should clicking an AI-credit-metered feature button
 * skip the sheet and pop the upsell overlay immediately?
 *
 * The answer is "yes" only when the user has nothing useful to gain
 * from opening the sheet:
 *
 *   - Free user, snapshot loaded, already at/over the monthly cap →
 *     opening would just bounce out with the same upsell on first
 *     generate click.
 *   - Firebase isn't configured (`status === "unavailable"`) → the AI
 *     callable can't be reached, the upsell renders the
 *     "sign-in isn't configured" footnote.
 *
 * Premium users always pass through. Loading and signed-out states
 * also pass through — the host shows a sign-in sheet before this gate,
 * and the "loading" window is short enough that gating it would just
 * flash the upsell on a fresh sign-in.
 *
 * Lifted out of the React component so the gate decision is unit
 * testable without standing up the full Zustand + Firestore graph.
 */
export function shouldShowAiUpsell(
  isPremium: boolean,
  snapshot: AiUsageSnapshot
): boolean {
  if (isPremium) return false;
  if (snapshot.status === "unavailable") return true;
  if (snapshot.status === "ready" && snapshot.atOrOverIncluded) return true;
  return false;
}

/**
 * Fold the server-returned `usage` envelope into the local snapshot
 * immediately, without waiting for the Firestore `onSnapshot` to fire.
 * Server snapshots usually arrive within ~500ms but the gap is visible
 * after a button click; this keeps the meter responsive.
 *
 * The optimistic value is cleared as soon as the Firestore snapshot
 * catches up (>= the optimistic count).
 */
export function recordOptimisticUsage(envelope: UsageResponseEnvelope): void {
  // Only ratchet upward — a stale envelope (e.g. from a retried request
  // whose server-side counter didn't move) should never drag the meter
  // backward.
  if (optimisticCount === null || envelope.countAfter > optimisticCount) {
    optimisticCount = envelope.countAfter;
    notify();
  }
}

/**
 * Thrown when the server rejects an AI call because the caller is at
 * their free-tier quota. The wire shape is `HttpsError("resource-exhausted",
 * "FREE_QUOTA_EXCEEDED")`; client wrappers translate that into this typed
 * error so the AI sheets can branch on it (free users see the upsell,
 * premium users never see this since they have no hard cap).
 */
export class AiQuotaExceededError extends Error {
  constructor() {
    super(
      "You're out of free credits this month. Upgrade to Premium for more."
    );
    this.name = "AiQuotaExceededError";
  }
}

/** Shown inline when a locally-overridden "Premium" client hits the
 *  server's free-tier cap. The upsell overlay auto-closes for
 *  `isPremium` users, so bouncing there just flashes a modal. */
export const PREMIUM_OVERRIDE_QUOTA_MESSAGE =
  "The local Premium override only changes the UI. Cloud Functions still see this account's real free-tier quota, which is used up for the month.";

const PREMIUM_CLIENT_QUOTA_FALLBACK_MESSAGE =
  "Something went wrong completing that request. Please try again.";

/**
 * Inline copy for `AiQuotaExceededError` / legacy premium-required when
 * the client already thinks it's Premium. Overridden clients get the
 * override explanation; a true Premium user (shouldn't hit this) gets a
 * generic retry — never the upsell, which would auto-close.
 */
export function quotaErrorMessageForPremiumClient(premium: {
  overridden: boolean;
}): string {
  return premium.overridden
    ? PREMIUM_OVERRIDE_QUOTA_MESSAGE
    : PREMIUM_CLIENT_QUOTA_FALLBACK_MESSAGE;
}

/** Wire message stamped by `checkAndReserveQuota`. Keep in lockstep with
 *  `functions/src/billing/usage.ts`. */
export const FREE_QUOTA_EXCEEDED = "FREE_QUOTA_EXCEEDED";

/** Wire message for an anonymous session that has spent its taste of the
 *  real AI (`BILLING_CONFIG.anonymousCredits`). Keep in lockstep with
 *  `functions/src/billing/usage.ts`. */
export const ANON_QUOTA_EXCEEDED = "ANON_QUOTA_EXCEEDED";
/** Wire message for an IP past the anonymous daily cap
 *  (`functions/src/billing/anonymousGuard.ts`). */
export const ANON_RATE_LIMITED = "ANON_RATE_LIMITED";

/**
 * Thrown when an anonymous session asks for more than its allowance. Not
 * a paywall: the fix is an account, which brings the monthly free
 * allowance — the AI sheets open the "Save your room" prompt.
 */
export class AiAnonymousQuotaExceededError extends Error {
  readonly reason: "quota" | "rate_limited";
  constructor(reason: "quota" | "rate_limited" = "quota") {
    super(
      reason === "rate_limited"
        ? "Too many free layouts from this network today. Save your room to keep going."
        : `That was your free layout. Save your room to get ${BILLING_CONFIG.freeMonthlyCredits} more credits every month.`
    );
    this.name = "AiAnonymousQuotaExceededError";
    this.reason = reason;
  }
}

/** Narrow a thrown value to the anonymous allowance / rate limit. */
export function anonymousQuotaReason(err: unknown): "quota" | "rate_limited" | null {
  if (!err || typeof err !== "object") return null;
  const o = err as { code?: unknown; message?: unknown; details?: unknown };
  const code = o.code;
  if (code !== "functions/resource-exhausted" && code !== "resource-exhausted") return null;
  const haystack = [o.message, o.details]
    .filter((v): v is string => typeof v === "string")
    .join(" ");
  if (haystack.includes(ANON_QUOTA_EXCEEDED)) return "quota";
  if (haystack.includes(ANON_RATE_LIMITED)) return "rate_limited";
  return null;
}

/**
 * Narrow a thrown value to the free-quota cap. The server signals that
 * with `HttpsError("resource-exhausted", "FREE_QUOTA_EXCEEDED")`.
 *
 * Matching on the code alone is too broad: `safeFetch` (and other
 * callables) also use `resource-exhausted` for oversized listing bodies,
 * which must surface as an inline import error rather than the Premium
 * upsell — otherwise a Premium user sees the sheet vanish (the upsell
 * auto-closes when `isPremium`).
 */
export function isQuotaExceededError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const o = err as { code?: unknown; message?: unknown; details?: unknown };
  const code = o.code;
  const isResourceExhausted =
    code === "functions/resource-exhausted" || code === "resource-exhausted";
  if (!isResourceExhausted) return false;
  const haystack = [o.message, o.details]
    .filter((v): v is string => typeof v === "string")
    .join(" ");
  return haystack.includes(FREE_QUOTA_EXCEEDED);
}

/** Test-only: reset the singleton so tests start fresh. */
export function _resetUsageForTests(): void {
  if (unsubAuth) unsubAuth();
  if (unsubDoc) unsubDoc();
  unsubAuth = null;
  unsubDoc = null;
  subscribed = false;
  rawUsage = INITIAL_RAW;
  optimisticCount = null;
  listeners = new Set();
}
